import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Responsive, WidthProvider } from 'react-grid-layout'
import GEmissionChart from './Component/Card/GEmissionCard'
import ELogementCard from './Component/Card/ELogementCard'
import ETransportCard from './Component/Card/ETransportCard'
import EShoppingCard from './Component/Card/EShoppingCard'
import EAlimentationCard from './Component/Card/EAlimentationCard'
import QuestionAnswerForm from './Component/Card/QuestionAnswerForm'
import { useTheme, Box } from '@mui/material'

import './style.css'
import { makeGetRequest } from 'utils/api'
import ConseilsCard from './Component/Card/ConseilsCard'
// LAP-752 — Two new analytical charts injected in the dashboard layout to
// give the user a sense of evolution and a comparison with the French
// average. They self-fetch from the new /api/Dashboard/emission-trend and
// /api/Dashboard/emission-vs-france endpoints.
import EcoTrendChart from './Component/Charts/EcoTrendChart'
import EcoVsFranceChart from './Component/Charts/EcoVsFranceChart'
// Module Contrats — consommations d'énergie extraites des factures par l'IA.
// Self-fetch depuis /api/contracts/consumption-summary/ (même trame LAP-752).
import EnergyConsumptionChart from './Component/Charts/EnergyConsumptionChart'

const ResponsiveGridLayout = WidthProvider(Responsive)

function MainDashBoard() {
  const theme = useTheme()
  const location = useLocation()
  const [reports, setReports] = useState([])

  // LAP-752 — auto-open the food emissions questionnaire at MainDashBoard
  // level when the user lands here from the wizard step `Footprint_food`.
  // The previous implementation relied on EAlimentationCard's own useEffect,
  // but that card is only mounted once the async /api/Dashboard/reports/
  // call returns an `emissionalimentation` entry — which a brand-new user
  // doesn't have yet, so the questionnaire never popped open and the user
  // had to refresh. Mounting the dialog here makes the trigger independent
  // of the report data.
  const [foodprintOpen, setFoodprintOpen] = useState(false)
  useEffect(() => {
    let triggered = false
    try {
      const params = new URLSearchParams(location.search)
      if (params.get('questionnaire') === 'alimentation') triggered = true
      if (typeof window !== 'undefined' && window.sessionStorage.getItem('w-footprint')) {
        triggered = true
      }
    } catch (_) {
      /* ignore */
    }
    if (triggered) setFoodprintOpen(true)
  }, [location.search])

  useEffect(() => {
    getDashboardReports()
  }, [])
  const updateBoard = () => {
    getDashboardReports()
  }
  const getDashboardReports = async () => {
    const { data, status } = await makeGetRequest('/api/Dashboard/reports/', {}, true)
    if (status === 200) setReports([...data])
  }
  const layout1 = [
    { i: '1', x: 0, y: 0, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '2', x: 3, y: 0, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '3', x: 6, y: 0, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '4', x: 9, y: 0, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '5', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '6', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '7', x: 6, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '8', x: 9, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
  ]
  const layout2 = [
    { i: '1', x: 0, y: 0, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '2', x: 5, y: 0, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '3', x: 0, y: 0, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '4', x: 5, y: 0, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '5', x: 0, y: 5, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '6', x: 5, y: 5, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '7', x: 0, y: 5, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '8', x: 5, y: 5, w: 5, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
  ]
  const layout3 = [
    { i: '1', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '2', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '3', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '4', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '5', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '6', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '7', x: 0, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '8', x: 3, y: 5, w: 3, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
  ]
  const layout4 = [
    { i: '1', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '2', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '3', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '4', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '5', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '6', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '7', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
    { i: '8', x: 0, y: 5, w: 4, h: 3, minW: 2, maxW: 6, minH: 3, maxH: 5 },
  ]
  const layouts = { lg: layout1, md: layout2, sm: layout3, xs: layout4 }

  const children = React.useMemo(() => {
    const xArr = [0, 6, 9, 0, 3, 6, 9, 0, 3, 6, 9, 0, 3, 6, 9, 0, 3, 6, 9, 0, 3, 6, 9]
    const baseTiles =
      (reports &&
        reports.map((val, idx) => (
          <div
            key={idx}
            className="hola"
            style={{ backgroundColor: theme.palette.appBar.main, display: 'grid' }}
            data-grid={{
              x: xArr[idx],
              y: Infinity,
              // Global and Conseils cards span 6 cols so every row fills the
              // 12-col grid: row1 = global 6 + logement 3 + transport 3 ;
              // row2 = shopping 3 + alimentation 3 + conseils 6 ; row3 =
              // trend 6 + vs-france 6. Previously Conseils was 3 cols wide so
              // the middle row only reached 9 cols, leaving a 3-col hole.
              w: val.type === 'globalemission' || val.type === 'conseils' ? 6 : 3,
              h: 3,
              minW: 2,
              maxW: 8,
              minH: 3,
              maxH: 10,
            }}
          >
            {val.type === 'globalemission' && <GEmissionChart oldData={val.data} />}
            {val.type === 'emissionlogement' && <ELogementCard oldData={val.data} />}
            {val.type === 'emissiontransport' && <ETransportCard oldData={val.data} />}
            {val.type === 'emissionshopping' && <EShoppingCard oldData={val.data} />}
            {val.type === 'emissionalimentation' && (
              <EAlimentationCard updateBoard={updateBoard} oldData={val.data} />
            )}
            {val.type === 'conseils' && <ConseilsCard oldData={val.data} />}
          </div>
        ))) ||
      []

    // LAP-752 — Append the two analytical charts as additional grid tiles.
    // They self-fetch so they don't bloat the /reports payload.
    const lap752Tiles = [
      <div
        key="lap752-trend"
        className="hola"
        style={{ backgroundColor: theme.palette.appBar.main, display: 'grid' }}
        data-grid={{ x: 0, y: Infinity, w: 6, h: 3, minW: 3, maxW: 12, minH: 3, maxH: 8 }}
      >
        <EcoTrendChart />
      </div>,
      <div
        key="lap752-vs-fr"
        className="hola"
        style={{ backgroundColor: theme.palette.appBar.main, display: 'grid' }}
        data-grid={{ x: 6, y: Infinity, w: 6, h: 3, minW: 3, maxW: 12, minH: 3, maxH: 8 }}
      >
        <EcoVsFranceChart />
      </div>,
      // Module Contrats — consommations d'énergie (électricité, gaz, eau…)
      <div
        key="contracts-energy"
        className="hola"
        style={{ backgroundColor: theme.palette.appBar.main, display: 'grid' }}
        data-grid={{ x: 0, y: Infinity, w: 6, h: 3, minW: 3, maxW: 12, minH: 3, maxH: 8 }}
      >
        <EnergyConsumptionChart />
      </div>,
    ]

    return [...baseTiles, ...lap752Tiles]
  }, [reports])
  return (
    <Box maxWidth={1650} ml={'auto'} mr={'auto '}>
      <ResponsiveGridLayout
        className="layout"
        style={{ margin: '10px' }}
        autoSize={true}
        layouts={layouts}
        useCSSTransforms={true}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        draggableCancel=".no-drag"
      >
        {children}
      </ResponsiveGridLayout>
      {/* LAP-752 — top-level food-emissions dialog. Renders only when the
          wizard sent the user here via `?questionnaire=alimentation` or set
          the `w-footprint` sessionStorage key, so it doesn't interfere with
          the dashboard for users who arrive via the side menu. The dialog
          itself is `open=true` from the start, bypassing the
          report-data-driven mount path inside EAlimentationCard. */}
      {foodprintOpen && (
        <QuestionAnswerForm
          open={foodprintOpen}
          setOpen={setFoodprintOpen}
          updateBoard={updateBoard}
        />
      )}
    </Box>
  )
}

export default MainDashBoard
